/**
 * Request validation (Phase 2b onboarding, Phase 2c offboarding): ONE module, used by POST /api/requests/onboarding
 * and /offboarding, by the tests, and imported as-is by the SPA (web/src/form.ts, web/src/offboarding.ts) so the
 * browser shows the server's exact messages. Keep it dependency-free: no Node or DOM APIs beyond Intl, no imports.
 *
 * Sources: canvas OnboardingScreen.pa.yaml OB_SaveBtn.OnSelect, validation block L243-266, and
 * OffboardingScreen.pa.yaml validation block L189-214 (messages verbatim except the date caps, which are 29 days
 * per ADR 058 §7.1). The server adds the checks that need the database (department exists, picker ids exist,
 * template exists) and Graph (people resolve to enabled accounts), reporting them in the same { field, message }
 * shape.
 */

export const PERSON_TYPES = ['Employee', 'Volunteer'] as const;
export const NEW_OR_TRANSFER = ['New', 'Transfer'] as const;
export const SOFTWARE_ITEMS = ['Adobe Acrobat Pro', 'Geopointe'] as const;
/** Onboarding checkboxes (canvas L1747-1846). "Other" is the equipOther boolean, not an item. */
export const ONBOARDING_EQUIPMENT = ['Laptop', 'Key Fob', 'Business Credit Card', 'Cubicle Keys', 'Gas Pin', 'Cell Phone'] as const;
/**
 * Offboarding "Equipment to Collect" boxes in form order (canvas OffboardingScreen L1752-2039 had seven; Scanner is
 * new in 2c, Marjorie's default: the 0002 enum and the task table already route it to IT). Stored values are the
 * equipment_item enum labels: the "Keys (office / cubicle)" box stores `Keys` (Facilities), never `Cubicle Keys`.
 */
export const OFFBOARDING_EQUIPMENT = ['Laptop', 'Cell Phone', 'Key Fob', 'Keys', 'Business Credit Card', 'Gas Pin', 'Scanner'] as const;
/** Canvas termination-time default (inventory §4.5: last day 14:00 Central) and the hour / minute control steps. */
export const DEFAULT_TERMINATION_TIME = '14:00';
export const TERMINATION_STEP_MINUTES = 15;

/** The canvas IsMatch pattern; also the 0002 check constraint on onboarding_details.phone_number. */
export const PHONE_PATTERN = /^\D*1?\D*(\d\D*){10}$/;
/** ADR 058 §7.1: the canvas said 30 and Flow 01 rejected > 29; the cap is 29 everywhere now. */
export const MAX_START_DAYS = 29;
export const TIME_ZONE = 'America/Chicago';
/** SharePoint single-line text columns held 255 characters; the same ceiling here. */
export const MAX_TEXT = 255;
const EXTENSION_PATTERN = /^\d{3}$/;
const EMAIL_PATTERN = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const MESSAGES = {
  firstName: 'First Name is required',
  lastName: 'Last Name is required',
  jobTitle: 'Job Title is required',
  department: 'Department is required',
  departmentUnknown: 'Department is not one of the listed departments',
  managerEmail: 'Manager Name is required',
  managerUnknown: 'Manager must be an active MOWCTX account (pick one from the search)',
  personType: 'Person Type is required',
  newOrTransfer: 'New or Transfer is required',
  startDay: 'Start Day is required',
  startDayInvalid: 'Start Day is not a valid date',
  startDayCap: `Start date cannot be more than ${MAX_START_DAYS} days from today.`,
  phoneNumber: 'Phone Number is required',
  phoneFormat: 'Phone Number must be a 10-digit US number',
  teamsExtension: 'Teams extension must be 3 digits (or blank for the next free number)',
  salesforceMirrorEmail: 'Salesforce mirror user must be an e-mail address',
  roleTemplate: 'Role template no longer exists; choose another or None',
  software: 'Software must be Adobe Acrobat Pro or Geopointe',
  equipment: 'Equipment item is not one of the onboarding options',
  sharepointSiteIds: 'A selected SharePoint site is no longer available; remove it and pick again',
  distroListIds: 'A selected distribution list is no longer available; remove it and pick again',
  sharedMailboxIds: 'A selected shared mailbox is no longer available; remove it and pick again',
  callQueueIds: 'A selected call queue is no longer available; remove it and pick again',
  tooLong: (label: string) => `${label} must be ${MAX_TEXT} characters or fewer`,
  // Offboarding (canvas OffboardingScreen L189-214, verbatim incl. the em dash).
  employeeEmail: 'Employee Account is required — select the employee from the M365 directory',
  employeeUnknown: 'Employee Account must be an active MOWCTX account (pick one from the search)',
  lastDay: 'Access termination date is required',
  lastDayInvalid: 'Access termination date is not a valid date',
  lastDayCap: `Last day cannot be more than ${MAX_START_DAYS} days from today.`,
  terminationTime: 'Access termination time must be HH:mm in 15-minute steps',
  emailAccessRecipient: 'Email access recipient is required',
  emailAccessRecipientUnknown: 'Email access recipient must be an active MOWCTX account (pick one from the search)',
  onedriveAccessRecipient: 'OneDrive access recipient is required',
  onedriveAccessRecipientUnknown: 'OneDrive access recipient must be an active MOWCTX account (pick one from the search)',
  /** New in 2c (not in the canvas): a delegate may not be the leaver. */
  delegateIsLeaver: (label: string) => `${label} cannot be the person being offboarded`,
  equipmentOffboarding: 'Equipment item is not one of the offboarding options',
  equipOtherDescription: 'Other equipment description is required when Other is ticked',
} as const;

/** Soft, non-blocking lines the forms show (never returned as errors). */
export const SOFT_MESSAGES = {
  startDayPast: 'Start day is in the past',
  terminationPast: 'This termination time has already passed; access will be terminated as soon as the request starts',
} as const;

export interface FieldError {
  field: string;
  message: string;
}

/** What the form posts. Every field optional at the type level: validation decides. */
export interface OnboardingDraft {
  roleTemplateId?: number | null;
  personType?: string;
  newOrTransfer?: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  phoneNumber?: string;
  department?: string;
  managerEmail?: string;
  jobTitle?: string;
  startDay?: string;
  officeLocation?: string;
  hasDirectReports?: boolean;
  salesforceAccess?: boolean;
  salesforceMirrorEmail?: string;
  teamsPhone?: boolean;
  teamsExtension?: string;
  sharepointSiteIds?: string[];
  distroListIds?: string[];
  sharedMailboxIds?: string[];
  callQueueIds?: string[];
  software?: string[];
  equipment?: string[];
  equipOther?: boolean;
  equipOtherDescription?: string;
  /** "Skip 72-hr hold": Super Admin only (the endpoint answers 403 to anyone else who sends true). */
  forceStart?: boolean;
  /** The user saw the duplicate warning and chose to continue. */
  confirmDuplicate?: boolean;
}

/** The validated, normalised request (blank optional text -> null; dependent fields dropped when their switch is off). */
export interface OnboardingValue {
  roleTemplateId: number | null;
  personType: (typeof PERSON_TYPES)[number];
  newOrTransfer: (typeof NEW_OR_TRANSFER)[number];
  firstName: string;
  lastName: string;
  displayName: string;
  preferredName: string | null;
  phoneNumber: string;
  department: string;
  managerEmail: string;
  jobTitle: string;
  startDay: string;
  officeLocation: string | null;
  hasDirectReports: boolean;
  salesforceAccess: boolean;
  salesforceMirrorEmail: string | null;
  teamsPhone: boolean;
  teamsExtension: string | null;
  sharepointSiteIds: string[];
  distroListIds: string[];
  sharedMailboxIds: string[];
  callQueueIds: string[];
  software: string[];
  equipment: string[];
  equipOther: boolean;
  equipOtherDescription: string | null;
  forceStart: boolean;
  confirmDuplicate: boolean;
}

/** Today's date in Central time as YYYY-MM-DD (ADR 058 §7.1: not UTC; at 7 pm CDT, UTC is already tomorrow). */
export function centralToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** Calendar arithmetic on a YYYY-MM-DD string (no time zone involved). */
export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function isIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

export function isUuid(s: string): boolean {
  return UUID_PATTERN.test(s);
}

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const bool = (v: unknown): boolean => v === true;
const list = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => str(x).trim()).filter(Boolean) : []);
const uniq = (xs: string[]): string[] => Array.from(new Set(xs));
const nullIfBlank = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/**
 * Pure validation in the canvas's order. `departments`, when given, is the active vocabulary (the server passes
 * the table; the SPA passes what GET /api/departments returned). Returns every error, not just the first.
 */
export function validateOnboarding(
  body: unknown,
  opts: { today: string; departments?: readonly string[] },
): { value: OnboardingValue | null; errors: FieldError[] } {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const errors: FieldError[] = [];
  const err = (field: string, message: string) => errors.push({ field, message });

  const firstName = str(b.firstName).trim();
  const lastName = str(b.lastName).trim();
  const jobTitle = str(b.jobTitle).trim();
  const department = str(b.department).trim();
  const managerEmail = str(b.managerEmail).trim().toLowerCase();
  const personType = str(b.personType).trim();
  const newOrTransfer = str(b.newOrTransfer).trim();
  const startDay = str(b.startDay).trim();
  const phoneNumber = str(b.phoneNumber).trim();

  // L243-266, in order.
  if (!firstName) err('firstName', MESSAGES.firstName);
  if (!lastName) err('lastName', MESSAGES.lastName);
  if (!jobTitle) err('jobTitle', MESSAGES.jobTitle);
  if (!department) err('department', MESSAGES.department);
  else if (opts.departments && !opts.departments.includes(department)) err('department', MESSAGES.departmentUnknown);
  if (!managerEmail) err('managerEmail', MESSAGES.managerEmail);
  else if (!EMAIL_PATTERN.test(managerEmail)) err('managerEmail', MESSAGES.managerUnknown);
  if (!personType || !(PERSON_TYPES as readonly string[]).includes(personType)) err('personType', MESSAGES.personType);
  if (!newOrTransfer || !(NEW_OR_TRANSFER as readonly string[]).includes(newOrTransfer)) err('newOrTransfer', MESSAGES.newOrTransfer);
  if (!startDay) err('startDay', MESSAGES.startDay);
  else if (!isIsoDate(startDay)) err('startDay', MESSAGES.startDayInvalid);
  if (!phoneNumber) err('phoneNumber', MESSAGES.phoneNumber);
  else if (!PHONE_PATTERN.test(phoneNumber)) err('phoneNumber', MESSAGES.phoneFormat);
  // The canvas had no lower bound (a past start day is accepted); only the cap.
  if (startDay && isIsoDate(startDay) && startDay > addDays(opts.today, MAX_START_DAYS)) err('startDay', MESSAGES.startDayCap);

  // Optional fields and the dependent ones (canvas: none of these were required).
  const preferredName = nullIfBlank(str(b.preferredName));
  const officeLocation = nullIfBlank(str(b.officeLocation));
  const teamsPhone = bool(b.teamsPhone);
  const salesforceAccess = bool(b.salesforceAccess);
  const equipOther = bool(b.equipOther);
  // Teams extension is written only when Teams phone is on (Patch L304); MaxLength 3 + "3 digits" hint (L2735-2738).
  const teamsExtension = teamsPhone ? nullIfBlank(str(b.teamsExtension)) : null;
  if (teamsExtension && !EXTENSION_PATTERN.test(teamsExtension)) err('teamsExtension', MESSAGES.teamsExtension);
  // The mirror picker shows only when Salesforce access is on (L3051).
  const salesforceMirrorEmail = salesforceAccess ? nullIfBlank(str(b.salesforceMirrorEmail).toLowerCase()) : null;
  if (salesforceMirrorEmail && !EMAIL_PATTERN.test(salesforceMirrorEmail)) err('salesforceMirrorEmail', MESSAGES.salesforceMirrorEmail);
  // The description box shows only when Other is ticked (L1883).
  const equipOtherDescription = equipOther ? nullIfBlank(str(b.equipOtherDescription)) : null;

  const lengths: [string, string, string | null][] = [
    ['firstName', 'First Name', firstName],
    ['lastName', 'Last Name', lastName],
    ['preferredName', 'Preferred Name', preferredName],
    ['jobTitle', 'Job Title', jobTitle],
    ['officeLocation', 'Office', officeLocation],
    ['phoneNumber', 'Phone Number', phoneNumber],
    ['equipOtherDescription', 'Other equipment description', equipOtherDescription],
  ];
  for (const [field, label, v] of lengths) {
    if (v && v.length > MAX_TEXT) err(field, MESSAGES.tooLong(label));
  }

  const software = uniq(list(b.software));
  if (software.some((s) => !(SOFTWARE_ITEMS as readonly string[]).includes(s))) err('software', MESSAGES.software);
  const equipment = uniq(list(b.equipment));
  if (equipment.some((s) => !(ONBOARDING_EQUIPMENT as readonly string[]).includes(s))) err('equipment', MESSAGES.equipment);

  const sharepointSiteIds = uniq(list(b.sharepointSiteIds));
  const distroListIds = uniq(list(b.distroListIds).map((x) => x.toLowerCase()));
  if (distroListIds.some((x) => !isUuid(x))) err('distroListIds', MESSAGES.distroListIds);
  const sharedMailboxIds = uniq(list(b.sharedMailboxIds).map((x) => x.toLowerCase()));
  // Call queues live in the Teams phone column (L2760): no Teams phone, no queues.
  const callQueueIds = teamsPhone ? uniq(list(b.callQueueIds).map((x) => x.toLowerCase())) : [];
  if (callQueueIds.some((x) => !isUuid(x))) err('callQueueIds', MESSAGES.callQueueIds);

  let roleTemplateId: number | null = null;
  if (b.roleTemplateId != null && b.roleTemplateId !== '') {
    const n = Number(b.roleTemplateId);
    if (!Number.isInteger(n) || n <= 0) err('roleTemplateId', MESSAGES.roleTemplate);
    else roleTemplateId = n;
  }

  if (errors.length) return { value: null, errors };
  return {
    value: {
      roleTemplateId,
      personType: personType as OnboardingValue['personType'],
      newOrTransfer: newOrTransfer as OnboardingValue['newOrTransfer'],
      firstName,
      lastName,
      displayName: `${firstName} ${lastName}`, // the canvas Title (Patch L283)
      preferredName,
      phoneNumber,
      department,
      managerEmail,
      jobTitle,
      startDay,
      officeLocation,
      hasDirectReports: bool(b.hasDirectReports),
      salesforceAccess,
      salesforceMirrorEmail,
      teamsPhone,
      teamsExtension,
      sharepointSiteIds,
      distroListIds,
      sharedMailboxIds,
      callQueueIds,
      software,
      equipment,
      equipOther,
      equipOtherDescription,
      forceStart: bool(b.forceStart),
      confirmDuplicate: bool(b.confirmDuplicate),
    },
    errors,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Offboarding (Phase 2c)
// ---------------------------------------------------------------------------------------------------------------

/** What the offboarding form posts. The leaver's display name and Entra id are resolved on the server, never read
 *  from the body. */
export interface OffboardingDraft {
  personType?: string;
  department?: string;
  /** The leaver (canvas `mow_employeeaccount`: Mail else UPN). */
  employeeEmail?: string;
  managerEmail?: string;
  /** The last day (canvas `mow_lastday` = the picked date). */
  lastDay?: string;
  /** Central wall time on the last day, `HH:mm` in 15-minute steps; default 14:00. */
  terminationTime?: string;
  emailAccessNeeded?: boolean;
  emailAccessRecipient?: string;
  onedriveAccessNeeded?: boolean;
  onedriveAccessRecipient?: string;
  equipment?: string[];
  equipOther?: boolean;
  equipOtherDescription?: string;
  forceStart?: boolean;
  confirmDuplicate?: boolean;
}

export interface OffboardingValue {
  personType: (typeof PERSON_TYPES)[number];
  department: string;
  employeeEmail: string;
  managerEmail: string;
  lastDay: string;
  terminationTime: string;
  /** ISO UTC instant: lastDay + terminationTime in America/Chicago, DST-safe. */
  accessTerminationAt: string;
  emailAccessNeeded: boolean;
  emailAccessRecipient: string | null;
  onedriveAccessNeeded: boolean;
  onedriveAccessRecipient: string | null;
  equipment: string[];
  equipOther: boolean;
  equipOtherDescription: string | null;
  forceStart: boolean;
  confirmDuplicate: boolean;
}

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isTerminationTime(s: string): boolean {
  const m = TIME_PATTERN.exec(s);
  return !!m && Number(m[2]) % TERMINATION_STEP_MINUTES === 0;
}

/** Every `HH:mm` the time select offers, 00:00 .. 23:45. */
export function terminationTimes(): string[] {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += TERMINATION_STEP_MINUTES) out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  return out;
}

/** Central's UTC offset in minutes at one instant (CDT -300, CST -360), from Intl's `longOffset` name. */
export function centralOffsetMinutes(instant: Date): number {
  const name =
    new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, timeZoneName: 'longOffset' })
      .formatToParts(instant)
      .find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  const m = /^GMT(?:([+-])(\d{1,2})(?::?(\d{2}))?)?$/.exec(name);
  if (!m || !m[1]) return 0;
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0));
}

/**
 * A Central wall time (YYYY-MM-DD + HH:mm) -> the UTC instant, DST-safe and dependency-free: the candidate
 * instants are the wall time minus each offset in force around that day; a candidate counts when Central formats
 * it back to the same wall time. An ambiguous fall-back time takes its first occurrence (the earlier instant, CDT);
 * a wall time that does not exist on the spring-forward day moves forward one hour. Inventory §4.5 / surprise 7.
 */
export function centralToUtc(isoDate: string, hhmm: string): string {
  const [y, mo, d] = isoDate.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi); // the wall clock read as if it were UTC
  const day = 24 * 60 * 60 * 1000;
  const offsets = Array.from(new Set([centralOffsetMinutes(new Date(wall - day)), centralOffsetMinutes(new Date(wall + day))]))
    .sort((a, b) => b - a); // larger offset (CDT, -300) first = the earlier instant
  for (const off of offsets) {
    const candidate = new Date(wall - off * 60_000);
    if (centralOffsetMinutes(candidate) === off) return candidate.toISOString();
  }
  // Nonexistent (spring forward): one hour later, in the offset that applies after the transition.
  const after = centralOffsetMinutes(new Date(wall + day));
  return new Date(wall + 60 * 60_000 - after * 60_000).toISOString();
}

/**
 * Pure validation in the canvas's order (OffboardingScreen L189-214), then the 2c rules: the 29-day cap on the last
 * day (no lower bound), the termination time, a delegate may not be the leaver, equipment from the offboarding
 * boxes, the Other description required only when Other is ticked. `accessTerminationAt` is computed here so the
 * server stores it and the SPA can warn when it is already past.
 */
export function validateOffboarding(
  body: unknown,
  opts: { today: string; departments?: readonly string[] },
): { value: OffboardingValue | null; errors: FieldError[] } {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const errors: FieldError[] = [];
  const err = (field: string, message: string) => errors.push({ field, message });

  const employeeEmail = str(b.employeeEmail).trim().toLowerCase();
  const department = str(b.department).trim();
  const managerEmail = str(b.managerEmail).trim().toLowerCase();
  const personType = str(b.personType).trim();
  const lastDay = str(b.lastDay).trim();
  const terminationTime = str(b.terminationTime).trim() || DEFAULT_TERMINATION_TIME;
  const emailAccessNeeded = bool(b.emailAccessNeeded);
  const onedriveAccessNeeded = bool(b.onedriveAccessNeeded);
  const emailAccessRecipient = emailAccessNeeded ? nullIfBlank(str(b.emailAccessRecipient).toLowerCase()) : null;
  const onedriveAccessRecipient = onedriveAccessNeeded ? nullIfBlank(str(b.onedriveAccessRecipient).toLowerCase()) : null;

  // L189-214, in order.
  if (!employeeEmail) err('employeeEmail', MESSAGES.employeeEmail);
  else if (!EMAIL_PATTERN.test(employeeEmail)) err('employeeEmail', MESSAGES.employeeUnknown);
  if (!department) err('department', MESSAGES.department);
  else if (opts.departments && !opts.departments.includes(department)) err('department', MESSAGES.departmentUnknown);
  if (!managerEmail) err('managerEmail', MESSAGES.managerEmail);
  else if (!EMAIL_PATTERN.test(managerEmail)) err('managerEmail', MESSAGES.managerUnknown);
  if (!personType || !(PERSON_TYPES as readonly string[]).includes(personType)) err('personType', MESSAGES.personType);
  if (!lastDay) err('lastDay', MESSAGES.lastDay);
  else if (!isIsoDate(lastDay)) err('lastDay', MESSAGES.lastDayInvalid);
  if (emailAccessNeeded && !emailAccessRecipient) err('emailAccessRecipient', MESSAGES.emailAccessRecipient);
  else if (emailAccessRecipient && !EMAIL_PATTERN.test(emailAccessRecipient)) err('emailAccessRecipient', MESSAGES.emailAccessRecipientUnknown);
  else if (emailAccessRecipient && employeeEmail && emailAccessRecipient === employeeEmail) {
    err('emailAccessRecipient', MESSAGES.delegateIsLeaver('Email access recipient'));
  }
  if (onedriveAccessNeeded && !onedriveAccessRecipient) err('onedriveAccessRecipient', MESSAGES.onedriveAccessRecipient);
  else if (onedriveAccessRecipient && !EMAIL_PATTERN.test(onedriveAccessRecipient)) err('onedriveAccessRecipient', MESSAGES.onedriveAccessRecipientUnknown);
  else if (onedriveAccessRecipient && employeeEmail && onedriveAccessRecipient === employeeEmail) {
    err('onedriveAccessRecipient', MESSAGES.delegateIsLeaver('OneDrive access recipient'));
  }

  // 2c rules. The canvas had no date window on offboarding; Flow 01 rejected > 29 days after the hold (inventory
  // §1.1 step 5), so the cap moves to submit time like the start day. No lower bound (a past last day is real).
  if (lastDay && isIsoDate(lastDay) && lastDay > addDays(opts.today, MAX_START_DAYS)) err('lastDay', MESSAGES.lastDayCap);
  if (!isTerminationTime(terminationTime)) err('terminationTime', MESSAGES.terminationTime);

  const equipOther = bool(b.equipOther);
  const equipOtherDescription = equipOther ? nullIfBlank(str(b.equipOtherDescription)) : null;
  if (equipOther && !equipOtherDescription) err('equipOtherDescription', MESSAGES.equipOtherDescription);
  else if (equipOtherDescription && equipOtherDescription.length > MAX_TEXT) {
    err('equipOtherDescription', MESSAGES.tooLong('Other equipment description'));
  }

  const equipment = uniq(list(b.equipment));
  if (equipment.some((s) => !(OFFBOARDING_EQUIPMENT as readonly string[]).includes(s))) err('equipment', MESSAGES.equipmentOffboarding);

  if (errors.length) return { value: null, errors };
  return {
    value: {
      personType: personType as OffboardingValue['personType'],
      department,
      employeeEmail,
      managerEmail,
      lastDay,
      terminationTime,
      accessTerminationAt: centralToUtc(lastDay, terminationTime),
      emailAccessNeeded,
      emailAccessRecipient,
      onedriveAccessNeeded,
      onedriveAccessRecipient,
      equipment,
      equipOther,
      equipOtherDescription,
      forceStart: bool(b.forceStart),
      confirmDuplicate: bool(b.confirmDuplicate),
    },
    errors,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Admin tabs (Phase 2d): Role Access people, Role Templates, Item & Task Assignments. Same contract as above: pure,
// import-free, every message the SPA shows comes from here. Canvas sources: AdminScreen.pa.yaml Save blocks
// L1670-1714 (Role Access), L899-935 (Role Templates), L2425-2457 (Item & Task Assignments); messages verbatim.
// The server-only rules (Super Admin changes, your own access, the last Super Admin) live in admin/roles.ts because
// they need the actor and the current rows; their messages are here so the SPA can mirror them.
// ---------------------------------------------------------------------------------------------------------------

/** access_role enum (0002); the SPA draws the seven boxes in the canvas order, this is the vocabulary. */
export const ACCESS_ROLE_VALUES = ['Super Admin', 'HR', 'Finance', 'IT', 'Facilities', 'Business Operations', 'Manager'] as const;
/** task_category enum (0002; inventory §4.3 lists all four, ADR 049 describes the first three). */
export const TASK_CATEGORIES = ['Equipment', 'Software', 'Service', 'Access'] as const;
/** task_role enum (0002) in the canvas dropdown order. */
export const TASK_ROLE_VALUES = ['IT', 'HR', 'Finance', 'Facilities', 'Business Operations'] as const;
/** Role Access e-mails must be MOWCTX addresses (canvas L1670-1714 EndsWith check). */
export const ORG_EMAIL_SUFFIX = '@mowctx.org';
/** Notes (role_access.notes, role_templates.notes) are multi-line; a sane ceiling rather than SharePoint's 255. */
export const MAX_NOTES = 1000;

export const ADMIN_MESSAGES = {
  // Role Access (canvas L1670-1714, verbatim)
  personRequired: 'Display name and email are required.',
  personDomain: 'Email must be a mowctx.org address.',
  personRoles: 'Select at least one role.',
  personRoleUnknown: 'Role is not one of the app roles',
  personNotesLong: `Notes must be ${MAX_NOTES} characters or fewer`,
  // Role Access, server-only (prompt defaults 2 and 3)
  superAdminOnly: 'Only a Super Admin can change Super Admin access',
  ownAccess: 'You cannot remove your own access',
  lastSuperAdmin: 'At least one active Super Admin must remain',
  personUnknown: 'No role access rows for that e-mail',
  // Role Templates (canvas L899-935, verbatim where the canvas had a message)
  templateTitle: 'Title is required.',
  templateTitleConflict: 'A template with this title already exists.',
  templateJobTitleConflict: 'A template for this Department + Job Title already exists.',
  templateDepartmentWideConflict: 'This department already has a department-wide template.',
  templateDepartment: 'Department is required',
  templateCallQueue: 'A call queue in this template is no longer in the call-queue list; remove it and pick again',
  templateCallQueueShape: 'Each call queue needs a resource-account address',
  templateUnknown: 'No such role template',
  // Item & Task Assignments (canvas L2425-2457, verbatim)
  assignmentRequired: 'Item name, category, and role are required.',
  assignmentConflict: 'An item with this name already exists.',
  assignmentCategory: 'Category is not one of the task categories',
  assignmentRole: 'Role is not one of the task roles',
  assignmentProtectedTitle: 'This item is used by the request rules; its name cannot be changed',
  assignmentProtectedDelete: 'This item is used by the request rules and cannot be deleted',
  assignmentUnknown: 'No such item',
} as const;

/** PUT /api/role-access/people/{email} body. */
export interface PersonDraft {
  userName?: string;
  roles?: string[];
  notes?: string | null;
}

export interface PersonValue {
  email: string;
  userName: string;
  roles: string[];
  /** undefined = the body did not send notes (leave the column alone); null = blank. */
  notes: string | null | undefined;
}

export function isOrgEmail(email: string): boolean {
  const e = email.trim().toLowerCase();
  return EMAIL_PATTERN.test(e) && e.endsWith(ORG_EMAIL_SUFFIX);
}

/**
 * Role Access person (canvas L1670-1714, in order): name + e-mail, the domain, at least one role; then the rules the
 * canvas lacked: roles from the enum, no duplicates, notes length. `email` comes from the route, lower-cased here.
 */
export function validatePerson(email: unknown, body: unknown): { value: PersonValue | null; errors: FieldError[] } {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const errors: FieldError[] = [];
  const err = (field: string, message: string) => errors.push({ field, message });

  const userName = str(b.userName).trim();
  const em = str(email).trim().toLowerCase();
  if (!userName || !em) err(!userName ? 'userName' : 'email', ADMIN_MESSAGES.personRequired);
  else if (!isOrgEmail(em)) err('email', ADMIN_MESSAGES.personDomain);
  const roles = uniq(list(b.roles));
  if (roles.length === 0) err('roles', ADMIN_MESSAGES.personRoles);
  else if (roles.some((r) => !(ACCESS_ROLE_VALUES as readonly string[]).includes(r))) err('roles', ADMIN_MESSAGES.personRoleUnknown);
  if (userName.length > MAX_TEXT) err('userName', MESSAGES.tooLong('Display name'));
  const notes = b.notes === undefined ? undefined : nullIfBlank(str(b.notes));
  if (notes && notes.length > MAX_NOTES) err('notes', ADMIN_MESSAGES.personNotesLong);

  if (errors.length) return { value: null, errors };
  return { value: { email: em, userName, roles: ACCESS_ROLE_VALUES.filter((r) => roles.includes(r)), notes }, errors };
}

/** POST / PUT /api/role-templates body: the row's writable columns + the three child lists. */
export interface TemplateDraft {
  title?: string;
  department?: string;
  jobTitle?: string;
  salesforceAccess?: boolean;
  salesforceMirrorEmail?: string;
  teamsPhone?: boolean;
  hasDirectReports?: boolean;
  creditCard?: boolean;
  gasPin?: boolean;
  keyFob?: boolean;
  cellPhone?: boolean;
  laptop?: boolean;
  cubicleKeys?: boolean;
  notes?: string;
  software?: string[];
  /** `label` is informational; the server writes the picker's label for the address. */
  callQueues?: { label?: string; address?: string }[];
  sharepointSiteIds?: string[];
}

export interface TemplateValue {
  title: string;
  department: string;
  /** '' = the department-wide default (prompt default 7; the canvas rule). */
  jobTitle: string;
  salesforceAccess: boolean;
  salesforceMirrorEmail: string | null;
  teamsPhone: boolean;
  hasDirectReports: boolean;
  creditCard: boolean;
  gasPin: boolean;
  keyFob: boolean;
  cellPhone: boolean;
  laptop: boolean;
  cubicleKeys: boolean;
  notes: string | null;
  software: string[];
  callQueueAddresses: string[];
  sharepointSiteIds: string[];
}

/**
 * Role Template (canvas L899-935: title required; the Department + Job Title clash is a 409 from the store). Plus:
 * department required and listed, job title optional (blank = department-wide), mirror optional but well-formed and
 * only kept when Salesforce access is on, call queues only when Teams phone is on, software from the enum, text caps.
 */
export function validateTemplate(body: unknown, opts: { departments?: readonly string[] } = {}): { value: TemplateValue | null; errors: FieldError[] } {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const errors: FieldError[] = [];
  const err = (field: string, message: string) => errors.push({ field, message });

  const title = str(b.title).trim();
  const department = str(b.department).trim();
  const jobTitle = str(b.jobTitle).trim();
  if (!title) err('title', ADMIN_MESSAGES.templateTitle);
  if (!department) err('department', ADMIN_MESSAGES.templateDepartment);
  else if (opts.departments && !opts.departments.includes(department)) err('department', MESSAGES.departmentUnknown);
  const salesforceAccess = bool(b.salesforceAccess);
  const teamsPhone = bool(b.teamsPhone);
  const salesforceMirrorEmail = salesforceAccess ? nullIfBlank(str(b.salesforceMirrorEmail).toLowerCase()) : null;
  if (salesforceMirrorEmail && !EMAIL_PATTERN.test(salesforceMirrorEmail)) err('salesforceMirrorEmail', MESSAGES.salesforceMirrorEmail);
  const notes = nullIfBlank(str(b.notes));
  const caps: [string, string, string | null][] = [
    ['title', 'Title', title],
    ['jobTitle', 'Job Title', jobTitle],
    ['salesforceMirrorEmail', 'Salesforce mirror user', salesforceMirrorEmail],
  ];
  for (const [field, label, v] of caps) {
    if (v && v.length > MAX_TEXT) err(field, MESSAGES.tooLong(label));
  }
  if (notes && notes.length > MAX_NOTES) err('notes', ADMIN_MESSAGES.personNotesLong);
  const software = uniq(list(b.software));
  if (software.some((s) => !(SOFTWARE_ITEMS as readonly string[]).includes(s))) err('software', MESSAGES.software);
  let callQueueAddresses: string[] = [];
  if (teamsPhone) {
    const raw = Array.isArray(b.callQueues) ? b.callQueues : [];
    const addresses = raw.map((q) => (q && typeof q === 'object' ? str((q as Record<string, unknown>).address) : str(q)).trim().toLowerCase());
    if (addresses.some((a) => !a || !EMAIL_PATTERN.test(a))) err('callQueues', ADMIN_MESSAGES.templateCallQueueShape);
    callQueueAddresses = uniq(addresses.filter(Boolean));
  }
  const sharepointSiteIds = uniq(list(b.sharepointSiteIds));

  if (errors.length) return { value: null, errors };
  return {
    value: {
      title,
      department,
      jobTitle,
      salesforceAccess,
      salesforceMirrorEmail,
      teamsPhone,
      hasDirectReports: bool(b.hasDirectReports),
      creditCard: bool(b.creditCard),
      gasPin: bool(b.gasPin),
      keyFob: bool(b.keyFob),
      cellPhone: bool(b.cellPhone),
      laptop: bool(b.laptop),
      cubicleKeys: bool(b.cubicleKeys),
      notes,
      software,
      callQueueAddresses,
      sharepointSiteIds,
    },
    errors,
  };
}

/** POST / PUT /api/task-role-assignments body. */
export interface AssignmentDraft {
  title?: string;
  category?: string;
  taskRole?: string;
}

export interface AssignmentValue {
  title: string;
  category: (typeof TASK_CATEGORIES)[number];
  taskRole: (typeof TASK_ROLE_VALUES)[number];
}

/** Item & Task Assignment (canvas L2425-2457): the three required, then the two vocabularies and the title cap. */
export function validateAssignment(body: unknown): { value: AssignmentValue | null; errors: FieldError[] } {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const errors: FieldError[] = [];
  const err = (field: string, message: string) => errors.push({ field, message });

  const title = str(b.title).trim();
  const category = str(b.category).trim();
  const taskRole = str(b.taskRole).trim();
  if (!title || !category || !taskRole) err(!title ? 'title' : !category ? 'category' : 'taskRole', ADMIN_MESSAGES.assignmentRequired);
  if (category && !(TASK_CATEGORIES as readonly string[]).includes(category)) err('category', ADMIN_MESSAGES.assignmentCategory);
  if (taskRole && !(TASK_ROLE_VALUES as readonly string[]).includes(taskRole)) err('taskRole', ADMIN_MESSAGES.assignmentRole);
  if (title.length > MAX_TEXT) err('title', MESSAGES.tooLong('Item name'));

  if (errors.length) return { value: null, errors };
  return { value: { title, category: category as AssignmentValue['category'], taskRole: taskRole as AssignmentValue['taskRole'] }, errors };
}
